/**
 * The expression language Ember models are written in (see model.ts): a small, side-effect-free
 * subset of JavaScript's expressions, so a model can say exactly what a drawing used to compute
 * and the frames come out the same to the bit.
 *
 *   numbers 0.5 1e-6, strings 'front', true false undefined null
 *   names and members  p.torso[1]  sk[side].knee  look.shirt.sleeves
 *   arithmetic  + - * / % **  (+ and - also add and subtract vectors; * and / scale one by a number)
 *   comparison  < <= > >= == != === !==   logic  && || ?? !   choice  a ? b : c
 *   arrays [a, b, ...rest]   objects { exponent: 2, ...extra }   calls f(a, ...rest)
 *   functions  q => q[1] > 0   (w, n) => tone(m, lit(n))
 *   array methods  .map .filter .some .every .find .includes .slice .concat .flat .indexOf .join .length
 *
 * There are no statements, assignments or loops: a model iterates with `map` over `range(n)` and
 * names its intermediate values in `let` lists. Only the functions and values a model is handed
 * exist; nothing reaches the host's globals, and constructors and prototypes are not reachable.
 *
 * Source is parsed once into a tree, which runs one of two ways with the same results: compiled to
 * JavaScript (generated only from the tree -- validated names, quoted strings, numbers, and this
 * file's own templates -- never from source text), which is what bakes frames at full speed; or,
 * where a page forbids generated code, walked by a closure interpreter.
 */

export type Scope = Record<string, any>
export type Compiled = (scope: Scope) => any

type Token = { t: "num"; v: number; at: number } | { t: "str"; v: string; at: number } | { t: "id"; v: string; at: number } | { t: "op"; v: string; at: number } | { t: "eof"; at: number }

const OPS = ["===", "!==", "...", "=>", "**", "??", "&&", "||", "==", "!=", "<=", ">=", "+", "-", "*", "/", "%", "<", ">", "!", "?", ":", "(", ")", "[", "]", "{", "}", ",", "."]
const NAME = /^[A-Za-z_$][A-Za-z0-9_$]*$/

export class ExprError extends Error {}

const tokenize = (src: string): Token[] => {
    const out: Token[] = []
    let i = 0
    while (i < src.length) {
        const c = src[i]
        if (/\s/.test(c)) { i++; continue }
        if (/[0-9]/.test(c) || (c === "." && /[0-9]/.test(src[i + 1] ?? ""))) {
            const m = /^(?:[0-9]+\.?[0-9]*|\.[0-9]+)(?:[eE][+-]?[0-9]+)?/.exec(src.slice(i))!
            out.push({ t: "num", v: Number(m[0]), at: i })
            i += m[0].length
            continue
        }
        if (/[A-Za-z_$]/.test(c)) {
            const m = /^[A-Za-z_$][A-Za-z0-9_$]*/.exec(src.slice(i))!
            out.push({ t: "id", v: m[0], at: i })
            i += m[0].length
            continue
        }
        if (c === "'" || c === "\"") {
            let j = i + 1, text = ""
            while (j < src.length && src[j] !== c) {
                if (src[j] === "\\") { text += src[j + 1] ?? ""; j += 2 } else text += src[j++]
            }
            if (j >= src.length) throw new ExprError(`Unterminated string at ${i}`)
            out.push({ t: "str", v: text, at: i })
            i = j + 1
            continue
        }
        const op = OPS.find(o => src.startsWith(o, i))
        if (!op) throw new ExprError(`Unexpected '${c}' at ${i}`)
        out.push({ t: "op", v: op, at: i })
        i += op.length
    }
    out.push({ t: "eof", at: src.length })
    return out
}

/** Names never reachable as members or names: the way out to Function and the host. (A function's own
 * members -- call, apply, bind -- are unreachable because functions have none here.) */
const FORBIDDEN = new Set(["constructor", "__proto__", "prototype", "__defineGetter__", "__defineSetter__", "__lookupGetter__", "__lookupSetter__"])
const ARRAY_METHODS = new Set(["map", "filter", "some", "every", "find", "findIndex", "includes", "slice", "concat", "flat", "indexOf", "join", "reduce"])
const KEYWORDS = new Set(["true", "false", "undefined", "null"])

// ---- the tree ------------------------------------------------------------------------------

type Item = { node: Node; spread: boolean }
export type Node =
    | { k: "lit"; v: unknown }
    | { k: "name"; name: string }
    | { k: "dot"; obj: Node; name: string }
    | { k: "index"; obj: Node; index: Node }
    | { k: "call"; fn: Node; args: Item[] }
    | { k: "neg"; a: Node }
    | { k: "not"; a: Node }
    | { k: "bin"; op: string; a: Node; b: Node }
    | { k: "cond"; c: Node; a: Node; b: Node }
    | { k: "array"; items: Item[] }
    | { k: "object"; entries: ({ key: string; value: Node } | { spread: Node })[] }
    | { k: "lambda"; params: string[]; body: Node }

const PRECEDENCE: string[][] = [["??"], ["||"], ["&&"], ["==", "!=", "===", "!=="], ["<", "<=", ">", ">="], ["+", "-"], ["*", "/", "%"]]

export const parseExpr = (src: string): Node => {
    const tokens = tokenize(src)
    let pos = 0
    const peek = () => tokens[pos]
    const isOp = (v: string, tok = peek()) => tok.t === "op" && tok.v === v
    const expect = (v: string) => {
        if (!isOp(v)) throw new ExprError(`Expected '${v}' at ${peek().at} in ${src}`)
        pos++
    }
    const checkName = (name: string, at: number) => {
        if (FORBIDDEN.has(name)) throw new ExprError(`'${name}' is not available (at ${at} in ${src})`)
        return name
    }
    // `x => ...` or `(a, b) => ...`: the parameter names, if a function starts here.
    const lambdaAhead = (): { params: string[]; end: number } | undefined => {
        const tok = peek()
        if (tok.t === "id" && isOp("=>", tokens[pos + 1])) return { params: [checkName(tok.v, tok.at)], end: pos + 2 }
        if (!isOp("(")) return undefined
        let depth = 0, j = pos
        for (; j < tokens.length; j++) {
            if (isOp("(", tokens[j])) depth++
            else if (isOp(")", tokens[j]) && --depth === 0) break
        }
        if (!isOp("=>", tokens[j + 1])) return undefined
        const params: string[] = []
        for (let k = pos + 1; k < j; k++) {
            const t = tokens[k]
            if (t.t === "id" && !KEYWORDS.has(t.v)) params.push(checkName(t.v, t.at))
            else if (!isOp(",", t)) throw new ExprError(`Bad parameter list at ${t.at} in ${src}`)
        }
        return { params, end: j + 2 }
    }
    const expression = (): Node => {
        const lambda = lambdaAhead()
        if (lambda) {
            pos = lambda.end
            return { k: "lambda", params: lambda.params, body: expression() }
        }
        const test = binary(0)
        if (!isOp("?")) return test
        pos++
        const a = expression()
        expect(":")
        return { k: "cond", c: test, a, b: expression() }
    }
    const binary = (level: number): Node => {
        if (level >= PRECEDENCE.length) return unary()
        let left = binary(level + 1)
        while (peek().t === "op" && PRECEDENCE[level].includes((peek() as any).v)) {
            const op = (tokens[pos++] as any).v as string
            left = { k: "bin", op, a: left, b: binary(level + 1) }
        }
        return left
    }
    const unary = (): Node => {
        if (isOp("-")) { pos++; return { k: "neg", a: unary() } }
        if (isOp("+")) { pos++; return unary() }
        if (isOp("!")) { pos++; return { k: "not", a: unary() } }
        const base = postfix()
        if (!isOp("**")) return base
        pos++
        return { k: "bin", op: "**", a: base, b: unary() }
    }
    const list = (close: string): Item[] => {
        const items: Item[] = []
        while (!isOp(close)) {
            const spread = isOp("...")
            if (spread) pos++
            items.push({ node: expression(), spread })
            if (!isOp(close)) expect(",")
        }
        pos++
        return items
    }
    const postfix = (): Node => {
        let node = primary()
        for (;;) {
            if (isOp(".")) {
                pos++
                const tok = tokens[pos++]
                if (tok.t !== "id") throw new ExprError(`Expected a name after '.' at ${tok.at} in ${src}`)
                node = { k: "dot", obj: node, name: checkName(tok.v, tok.at) }
            } else if (isOp("[")) {
                pos++
                const index = expression()
                expect("]")
                node = { k: "index", obj: node, index }
            } else if (isOp("(")) {
                pos++
                node = { k: "call", fn: node, args: list(")") }
            } else return node
        }
    }
    const primary = (): Node => {
        const tok = tokens[pos++]
        if (tok.t === "num" || tok.t === "str") return { k: "lit", v: tok.v }
        if (tok.t === "id") {
            if (tok.v === "true") return { k: "lit", v: true }
            if (tok.v === "false") return { k: "lit", v: false }
            if (tok.v === "undefined") return { k: "lit", v: undefined }
            if (tok.v === "null") return { k: "lit", v: null }
            return { k: "name", name: checkName(tok.v, tok.at) }
        }
        if (tok.t === "op" && tok.v === "(") {
            const inner = expression()
            expect(")")
            return inner
        }
        if (tok.t === "op" && tok.v === "[") return { k: "array", items: list("]") }
        if (tok.t === "op" && tok.v === "{") {
            const entries: ({ key: string; value: Node } | { spread: Node })[] = []
            while (!isOp("}")) {
                if (isOp("...")) { pos++; entries.push({ spread: expression() }) }
                else {
                    const k = tokens[pos++]
                    if (k.t !== "id" && k.t !== "str") throw new ExprError(`Expected a key at ${k.at} in ${src}`)
                    const key = checkName(k.v, k.at)
                    if (isOp(":")) { pos++; entries.push({ key, value: expression() }) }
                    else if (k.t === "id") entries.push({ key, value: { k: "name", name: key } })
                    else throw new ExprError(`Expected ':' after '${key}' in ${src}`)
                }
                if (!isOp("}")) expect(",")
            }
            pos++
            return { k: "object", entries }
        }
        throw new ExprError(`Unexpected ${tok.t === "eof" ? "end" : `'${(tok as any).v}'`} at ${tok.at} in ${src}`)
    }
    const result = expression()
    if (peek().t !== "eof") throw new ExprError(`Unexpected '${(peek() as any).v}' at ${peek().at} in ${src}`)
    return result
}

// ---- semantics shared by both back ends -----------------------------------------------------

const isVec = (v: any): boolean => Array.isArray(v) || ArrayBuffer.isView(v)

const member = (obj: any, name: any): any => {
    if (obj === null || obj === undefined) throw new ExprError(`Cannot read '${String(name)}' of ${obj}`)
    if (typeof name !== "string" && typeof name !== "number") throw new ExprError(`Bad member ${String(name)}`)
    if (typeof name === "string" && FORBIDDEN.has(name)) throw new ExprError(`'${name}' is not available`)
    if (typeof obj === "function") throw new ExprError(`Functions have no members (${String(name)})`)
    if (isVec(obj)) {
        const list = obj as any[]
        if (name === "length") return list.length
        if (typeof name === "number" || /^[0-9]+$/.test(name)) return list[name as number]
        if (ARRAY_METHODS.has(name)) return (list as any)[name].bind(list)
        throw new ExprError(`Arrays have no '${name}'`)
    }
    if (typeof obj === "string") {
        if (name === "length") return obj.length
        if (name === "startsWith" || name === "endsWith" || name === "includes") return (obj as any)[name].bind(obj)
        throw new ExprError(`Strings have no '${name}'`)
    }
    if (typeof obj !== "object") throw new ExprError(`${typeof obj} has no '${name}'`)
    return Object.prototype.hasOwnProperty.call(obj, name) ? obj[name] : undefined
}

const hasOwn = Object.prototype.hasOwnProperty
/** `obj.name` for a name the parser has already checked: plain objects straight off, the rest as member. */
const dot = (obj: any, name: string): any =>
    obj !== null && typeof obj === "object" && !Array.isArray(obj) && !ArrayBuffer.isView(obj) ? (hasOwn.call(obj, name) ? obj[name] : undefined) : member(obj, name)

const add = (a: any, b: any) => {
    if (typeof a === "number" && typeof b === "number") return a + b
    if (isVec(a) && isVec(b)) return [a[0] + b[0], a[1] + b[1], a[2] + b[2]]
    if (typeof a === "string" && typeof b === "string") return a + b
    throw new ExprError(`Cannot add ${typeof a} and ${typeof b}`)
}
const sub = (a: any, b: any) => {
    if (typeof a === "number" && typeof b === "number") return a - b
    if (isVec(a) && isVec(b)) return [a[0] - b[0], a[1] - b[1], a[2] - b[2]]
    throw new ExprError(`Cannot subtract ${typeof b} from ${typeof a}`)
}
const mul = (a: any, b: any) => {
    if (typeof a === "number" && typeof b === "number") return a * b
    if (isVec(a) && typeof b === "number") return [a[0] * b, a[1] * b, a[2] * b]
    if (typeof a === "number" && isVec(b)) return [a * b[0], a * b[1], a * b[2]]
    throw new ExprError(`Cannot multiply ${typeof a} by ${typeof b}`)
}
const div = (a: any, b: any) => {
    if (typeof a === "number" && typeof b === "number") return a / b
    if (isVec(a) && typeof b === "number") return [a[0] / b, a[1] / b, a[2] / b]
    throw new ExprError(`Cannot divide ${typeof a} by ${typeof b}`)
}
const neg = (v: any) => {
    if (typeof v === "number") return -v
    if (isVec(v)) return [-v[0], -v[1], -v[2]]
    throw new ExprError(`Cannot negate ${typeof v}`)
}
const lookup = (scope: Scope, name: string) => {
    const v = scope[name]
    if (v === undefined && !(name in scope)) throw new ExprError(`Unknown name '${name}'`)
    return v
}
const num = (op: string, f: (a: number, b: number) => any) => (a: any, b: any) => {
    if (typeof a !== "number" || typeof b !== "number") throw new ExprError(`'${op}' needs numbers`)
    return f(a, b)
}
const BINARY: Record<string, (a: any, b: any) => any> = {
    "+": add, "-": sub, "*": mul, "/": div,
    "%": num("%", (a, b) => a % b), "**": num("**", (a, b) => a ** b),
    "<": num("<", (a, b) => a < b), "<=": num("<=", (a, b) => a <= b), ">": num(">", (a, b) => a > b), ">=": num(">=", (a, b) => a >= b),
    "==": (a, b) => a === b, "===": (a, b) => a === b, "!=": (a, b) => a !== b, "!==": (a, b) => a !== b,
}

// ---- back end 1: closures ------------------------------------------------------------------

const spreadInto = (items: { node: Compiled; spread: boolean }[], s: Scope) => {
    const out: any[] = []
    for (const it of items) {
        const v = it.node(s)
        if (it.spread) {
            if (!isVec(v)) throw new ExprError("Only an array can be spread")
            const list = v as ArrayLike<unknown>
            for (let i = 0; i < list.length; i++) out.push(list[i])
        } else out.push(v)
    }
    return out
}

export const interpret = (node: Node): Compiled => {
    switch (node.k) {
        case "lit": { const v = node.v; return () => v }
        case "name": { const name = node.name; return s => lookup(s, name) }
        case "dot": { const obj = interpret(node.obj), name = node.name; return s => dot(obj(s), name) }
        case "index": { const obj = interpret(node.obj), index = interpret(node.index); return s => member(obj(s), index(s)) }
        case "call": {
            const fn = interpret(node.fn), args = node.args.map(a => ({ node: interpret(a.node), spread: a.spread }))
            return s => {
                const f = fn(s)
                if (typeof f !== "function") throw new ExprError("Not a function")
                return f(...spreadInto(args, s))
            }
        }
        case "neg": { const a = interpret(node.a); return s => neg(a(s)) }
        case "not": { const a = interpret(node.a); return s => !a(s) }
        case "cond": { const c = interpret(node.c), a = interpret(node.a), b = interpret(node.b); return s => (c(s) ? a(s) : b(s)) }
        case "bin": {
            const a = interpret(node.a), b = interpret(node.b)
            if (node.op === "&&") return s => a(s) && b(s)
            if (node.op === "||") return s => a(s) || b(s)
            if (node.op === "??") return s => a(s) ?? b(s)
            const f = BINARY[node.op]
            return s => f(a(s), b(s))
        }
        case "array": { const items = node.items.map(a => ({ node: interpret(a.node), spread: a.spread })); return s => spreadInto(items, s) }
        case "object": {
            const entries = node.entries.map(e => ("spread" in e ? { spread: interpret(e.spread) } : { key: e.key, value: interpret(e.value) }))
            return s => {
                const out: Record<string, any> = {}
                for (const e of entries) {
                    if ("spread" in e) {
                        const v = e.spread(s)
                        if (v && typeof v === "object") for (const k of Object.keys(v)) if (!FORBIDDEN.has(k)) out[k] = v[k]
                    } else out[e.key] = e.value(s)
                }
                return out
            }
        }
        case "lambda": {
            const params = node.params, body = interpret(node.body)
            return scope => (...args: any[]) => {
                const inner = Object.create(scope)
                for (let i = 0; i < params.length; i++) inner[params[i]] = args[i]
                return body(inner)
            }
        }
    }
}

// ---- back end 2: JavaScript ----------------------------------------------------------------

/** A function with steps (model.ts FnSpec), its values as source, nested functions, or literal data. */
export interface FnSource {
    fn: string[]
    let?: [string, unknown][]
    cases?: [string, unknown][]
    return?: unknown
}
const isFn = (v: unknown): v is FnSource => !!v && typeof v === "object" && !Array.isArray(v) && Array.isArray((v as any).fn)

const local = (name: string) => `v_${name}`
const helpers = { $g: lookup, $m: member, $d: dot, $add: add, $sub: sub, $mul: mul, $div: div, $neg: neg }
const PRELUDE = `"use strict"; const { $g, $m, $d, $add, $sub, $mul, $div, $neg } = $h;`

interface Unit { literals: unknown[] }
const literal = (unit: Unit, v: unknown) => `$L[${unit.literals.push(v) - 1}]`

const gen = (node: Node, locals: Set<string>, unit: Unit): string => {
    const g = (n: Node) => gen(n, locals, unit)
    const items = (list: Item[]) => list.map(i => (i.spread ? `...(${g(i.node)})` : `(${g(i.node)})`)).join(", ")
    switch (node.k) {
        case "lit": {
            const v = node.v
            if (typeof v === "number") return Number.isFinite(v) ? String(v) : v !== v ? "NaN" : v > 0 ? "(1/0)" : "(-1/0)"
            if (typeof v === "string") return JSON.stringify(v)
            return v === undefined ? "undefined" : v === null ? "null" : v ? "true" : "false"
        }
        case "name":
            if (!NAME.test(node.name) || FORBIDDEN.has(node.name)) throw new ExprError(`Bad name ${node.name}`)
            return locals.has(node.name) ? local(node.name) : `$g($s, ${JSON.stringify(node.name)})`
        case "dot":
            if (!NAME.test(node.name) || FORBIDDEN.has(node.name)) throw new ExprError(`Bad member ${node.name}`)
            return `$d(${g(node.obj)}, ${JSON.stringify(node.name)})`
        case "index": return `$m(${g(node.obj)}, ${g(node.index)})`
        case "call": return `(${g(node.fn)})(${items(node.args)})`
        case "neg": return `$neg(${g(node.a)})`
        case "not": return `(!(${g(node.a)}))`
        case "cond": return `((${g(node.c)}) ? (${g(node.a)}) : (${g(node.b)}))`
        case "bin": {
            const a = g(node.a), b = g(node.b)
            switch (node.op) {
                case "+": return `$add(${a}, ${b})`
                case "-": return `$sub(${a}, ${b})`
                case "*": return `$mul(${a}, ${b})`
                case "/": return `$div(${a}, ${b})`
                case "==": return `((${a}) === (${b}))`
                case "!=": return `((${a}) !== (${b}))`
                case "%": case "**": case "<": case "<=": case ">": case ">=": case "===": case "!==": case "&&": case "||": case "??":
                    return `((${a}) ${node.op} (${b}))`
            }
            throw new ExprError(`Unknown operator ${node.op}`)
        }
        case "array": return `[${items(node.items)}]`
        case "object":
            return `({ ${node.entries.map(e => ("spread" in e ? `...(${g(e.spread)})` : `${JSON.stringify(e.key)}: (${g(e.value)})`)).join(", ")} })`
        case "lambda": {
            for (const p of node.params) if (!NAME.test(p) || FORBIDDEN.has(p)) throw new ExprError(`Bad parameter ${p}`)
            const inner = new Set([...locals, ...node.params])
            return `((${node.params.map(local).join(", ")}) => (${gen(node.body, inner, unit)}))`
        }
    }
}

const genValue = (value: unknown, locals: Set<string>, unit: Unit): string =>
    typeof value === "string" ? gen(parseExpr(value), locals, unit) : isFn(value) ? genFn(value, locals, unit) : literal(unit, deepFreeze(clone(value)))

const genFn = (spec: FnSource, locals: Set<string>, unit: Unit): string => {
    const names = [...spec.fn, ...(spec.let ?? []).map(([n]) => n)]
    for (const n of names) if (typeof n !== "string" || !NAME.test(n) || FORBIDDEN.has(n)) throw new ExprError(`Bad name ${String(n)}`)
    const inner = new Set([...locals, ...names])
    const lets = (spec.let ?? []).map(([n, v]) => `${local(n)} = ${genValue(v, inner, unit)};`).join(" ")
    const declared = (spec.let ?? []).length ? `let ${(spec.let ?? []).map(([n]) => local(n)).join(", ")};` : ""
    const cases = (spec.cases ?? []).map(([c, v]) => `if (${gen(parseExpr(c), inner, unit)}) return ${genValue(v, inner, unit)};`).join(" ")
    const result = spec.return === undefined ? "undefined" : genValue(spec.return, inner, unit)
    return `(function (${spec.fn.map(local).join(", ")}) { ${declared} ${lets} ${cases} return ${result}; })`
}

const clone = (v: unknown) => (v === undefined ? undefined : JSON.parse(JSON.stringify(v)))
const deepFreeze = <T>(v: T): T => {
    if (v && typeof v === "object") {
        Object.values(v as any).forEach(deepFreeze)
        Object.freeze(v)
    }
    return v
}

/** Whether this page lets us generate code (a strict content-security policy may not). */
let generated: boolean | undefined
const canGenerate = (): boolean => {
    if (generated === undefined) {
        try { generated = new Function("return 1")() === 1 } catch { generated = false }
    }
    return generated
}
/** Force the closure interpreter (tests compare the two). */
export const setExprBackend = (backend: "js" | "closures" | "auto"): void => {
    generated = backend === "auto" ? undefined : backend === "js"
    cache.clear()
    fnCache.clear()
}

const build = (body: string, unit: Unit): Compiled =>
    new Function("$h", "$L", `${PRELUDE} return ($s) => (${body});`)(helpers, unit.literals) as Compiled

// ---- entry points --------------------------------------------------------------------------

export const compileExpr = (src: string): Compiled => {
    const tree = parseExpr(src)
    if (!canGenerate()) return interpret(tree)
    const unit: Unit = { literals: [] }
    return build(gen(tree, new Set(), unit), unit)
}

const cache = new Map<string, Compiled>()
/** compileExpr, remembered: models share many identical expressions. */
export const expr = (src: string): Compiled => {
    let c = cache.get(src)
    if (!c) {
        c = compileExpr(src)
        cache.set(src, c)
    }
    return c
}

const interpretValue = (value: unknown, path: string): Compiled => {
    if (typeof value === "string") return interpret(parseExpr(value))
    if (isFn(value)) return interpretFn(value, path)
    const v = deepFreeze(clone(value))
    return () => v
}
const interpretFn = (spec: FnSource, path: string): Compiled => {
    const params = spec.fn
    const lets = (spec.let ?? []).map(([n, v]) => [n, interpretValue(v, path)] as const)
    const cases = (spec.cases ?? []).map(([c, v]) => [interpret(parseExpr(c)), interpretValue(v, path)] as const)
    const result = spec.return === undefined ? undefined : interpretValue(spec.return, path)
    return scope => (...args: any[]) => {
        const s = Object.create(scope)
        for (let i = 0; i < params.length; i++) s[params[i]] = args[i]
        for (let i = 0; i < lets.length; i++) s[lets[i][0]] = lets[i][1](s)
        for (let i = 0; i < cases.length; i++) if (cases[i][0](s)) return cases[i][1](s)
        return result ? result(s) : undefined
    }
}

const fnCache = new Map<string, Compiled>()
/** A function with steps, compiled: evaluated in a scope, it is the function. */
export const compileFnSource = (spec: FnSource, path = "fn"): Compiled => {
    const key = JSON.stringify(spec)
    let c = fnCache.get(key)
    if (c) return c
    if (canGenerate()) {
        const unit: Unit = { literals: [] }
        c = build(genFn(spec, new Set(), unit), unit)
    } else c = interpretFn(spec, path)
    fnCache.set(key, c)
    return c
}
